import { useState, Fragment } from "react";
import { Link } from "react-router-dom";
import { Shield, FileText, ChatCircle } from "@phosphor-icons/react";
import Breadcrumbs from "../components/Breadcrumbs";
import AppNav from "../components/AppNav";
import AppFooter from "../components/AppFooter";
import PageMeta from "../components/PageMeta";
import JsonLd, { collectionPageSchema } from "../components/JsonLd";

export default function CommunicationGuides() {
  const [activeTab, setActiveTab] = useState(0);

  return (
    <div
      className="min-h-screen bg-gradient-to-b from-orange-50 to-white dark:from-gray-900 dark:to-gray-900"
    >
      <PageMeta
        title="Doctor Communication Guides"
        description="Learn how to communicate effectively with your healthcare providers. Guides for discussing endometriosis, PCOS, chronic pain, and getting the care you deserve."
        path="/communication-guides"
      />
      <JsonLd data={collectionPageSchema({ name: 'Doctor Communication Guides', description: 'Guides for effectively communicating with your healthcare providers about endometriosis, PCOS, chronic pain, and other women\'s health conditions.', url: 'https://romyhealth.co/communication-guides' })} />
      <AppNav />

      {/* Main Content */}
      <main className="pt-16 pb-16 md:pb-0 min-h-screen">
        <div className="max-w-5xl mx-auto px-6 lg:px-8 py-12">
          <Breadcrumbs current="Guides" />

          {/* Page Header */}
          <div className="text-center mb-12">
            <div className="flex justify-center mb-4">
              <ChatCircle size={64} className="text-pink-500" weight="fill" />
            </div>
            <h1 className="text-4xl lg:text-5xl font-bold mb-4">
              <span className="text-pink-500">Communication</span>{" "}
              <span className="text-gray-800 dark:text-gray-100">Guides</span>
            </h1>
            <p className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto leading-relaxed">
              Learn effective strategies for advocating for your health, communicating symptoms clearly, and requesting the care you need.
            </p>
          </div>

          {/* Tab Navigation */}
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-lg border border-gray-100 dark:border-gray-700 mb-8 overflow-hidden">
            <div className="flex flex-col md:flex-row border-b border-gray-200 dark:border-gray-700">
              <button
                onClick={() => setActiveTab(0)}
                className={`flex-1 px-6 py-4 text-left font-semibold transition-all ${
                  activeTab === 0
                    ? "bg-pink-50 dark:bg-pink-950 text-pink-600 dark:text-pink-400 border-b-2 border-pink-500"
                    : "text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
                }`}
              >
                <div className="flex items-center space-x-2">
                  <Shield size={20} weight={activeTab === 0 ? "fill" : "regular"} />
                  <span>Being Taken Seriously</span>
                </div>
              </button>
              <button
                onClick={() => setActiveTab(1)}
                className={`flex-1 px-6 py-4 text-left font-semibold transition-all ${
                  activeTab === 1
                    ? "bg-pink-50 dark:bg-pink-950 text-pink-600 dark:text-pink-400 border-b-2 border-pink-500"
                    : "text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
                }`}
              >
                <div className="flex items-center space-x-2">
                  <ChatCircle size={20} weight={activeTab === 1 ? "fill" : "regular"} />
                  <span>Communicating Symptoms</span>
                </div>
              </button>
              <button
                onClick={() => setActiveTab(2)}
                className={`flex-1 px-6 py-4 text-left font-semibold transition-all ${
                  activeTab === 2
                    ? "bg-pink-50 dark:bg-pink-950 text-pink-600 dark:text-pink-400 border-b-2 border-pink-500"
                    : "text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
                }`}
              >
                <div className="flex items-center space-x-2">
                  <FileText size={20} weight={activeTab === 2 ? "fill" : "regular"} />
                  <span>Requesting Tests</span>
                </div>
              </button>
            </div>
          </div>

          {/* Tab Content */}
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-lg p-8 lg:p-12 border border-gray-100 dark:border-gray-700">
            {activeTab === 0 ? (
              <Fragment>
                <div className="mb-6">
                  <h2 className="text-3xl font-bold text-gray-800 dark:text-gray-100 mb-2">
                    Advocating for Proper Care & Being Taken Seriously
                  </h2>
                  <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed">
                    What to do when you feel dismissed, rushed, or unheard.
                  </p>
                </div>
            <div className="bg-pink-50 dark:bg-pink-950 rounded-xl p-6 mb-8 border border-pink-200 dark:border-pink-800">
              <p className="text-base font-semibold text-gray-800 dark:text-gray-100 mb-2">Goal:</p>
              <p className="text-gray-700 dark:text-gray-300">
                Maintain ownership of your care, especially in cases of dismissal, gender bias, or diagnostic delay.
              </p>
            </div>

            {/* Calm Authority Section */}
            <div className="mb-10">
              <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-4">
                Calm Authority &gt; Emotional Escalation
              </h2>
              
              {/* POWER Statements */}
              <div className="mb-8">
                <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-4">Use POWER Statements</h3>
                <div className="space-y-4">
                  <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 border-l-4 border-orange-500">
                    <p className="font-bold text-gray-800 dark:text-gray-100 mb-1">
                      <span className="text-orange-600">P</span> — Pause & Reframe
                    </p>
                    <p className="text-gray-700 dark:text-gray-300 italic">"I want to make sure I'm explaining this clearly."</p>
                  </div>
                  <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 border-l-4 border-orange-500">
                    <p className="font-bold text-gray-800 dark:text-gray-100 mb-1">
                      <span className="text-orange-600">O</span> — Own the Data
                    </p>
                    <p className="text-gray-700 dark:text-gray-300 italic">"I've been tracking this consistently for ___ months."</p>
                  </div>
                  <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 border-l-4 border-orange-500">
                    <p className="font-bold text-gray-800 dark:text-gray-100 mb-1">
                      <span className="text-orange-600">W</span> — Widen the Frame
                    </p>
                    <p className="text-gray-700 dark:text-gray-300 italic">"Given the duration and impact, I'm concerned about missing something."</p>
                  </div>
                  <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 border-l-4 border-orange-500">
                    <p className="font-bold text-gray-800 dark:text-gray-100 mb-1">
                      <span className="text-orange-600">E</span> — Explicit Needs
                    </p>
                    <p className="text-gray-700 dark:text-gray-300 italic">"What I need from today's visit is clarity on next steps."</p>
                  </div>
                  <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 border-l-4 border-orange-500">
                    <p className="font-bold text-gray-800 dark:text-gray-100 mb-1">
                      <span className="text-orange-600">R</span> — Request Accountability
                    </p>
                    <p className="text-gray-700 dark:text-gray-300 italic">"What's our plan if this doesn't improve?"</p>
                  </div>
                </div>
              </div>

              {/* Dismissal-Response Scripts */}
              <div className="mb-8">
                <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-4">Dismissal-Response Scripts</h3>
                <div className="space-y-4">
                  <div className="bg-white dark:bg-gray-900 rounded-lg p-5 border border-gray-200 dark:border-gray-700 shadow-sm">
                    <p className="font-semibold text-gray-800 dark:text-gray-100 mb-2">If told "this is normal":</p>
                    <p className="text-gray-700 dark:text-gray-300 italic">"Normal for whom? At what point does it become abnormal?"</p>
                  </div>
                  <div className="bg-white dark:bg-gray-900 rounded-lg p-5 border border-gray-200 dark:border-gray-700 shadow-sm">
                    <p className="font-semibold text-gray-800 dark:text-gray-100 mb-2">If told "it's stress/anxiety":</p>
                    <p className="text-gray-700 dark:text-gray-300 italic">"I'm open to that being a factor, but I'd like to rule out physical causes as well."</p>
                  </div>
                  <div className="bg-white dark:bg-gray-900 rounded-lg p-5 border border-gray-200 dark:border-gray-700 shadow-sm">
                    <p className="font-semibold text-gray-800 dark:text-gray-100 mb-2">If rushed:</p>
                    <p className="text-gray-700 dark:text-gray-300 italic">"Before we wrap up, I want to be sure we've addressed my main concern."</p>
                  </div>
                </div>
              </div>
            </div>

            {/* When to Escalate */}
            <div className="mb-8 bg-orange-50 dark:bg-orange-950 rounded-xl p-6 border border-orange-200 dark:border-orange-800">
              <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-4">When to Escalate</h2>
              <ul className="space-y-2 text-gray-700 dark:text-gray-300">
                <li className="flex items-start space-x-2">
                  <span className="flex-shrink-0 w-2 h-2 rounded-full bg-orange-500 mt-2"></span>
                  <span>Repeated dismissal</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="flex-shrink-0 w-2 h-2 rounded-full bg-orange-500 mt-2"></span>
                  <span>Symptoms worsening despite reassurance</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="flex-shrink-0 w-2 h-2 rounded-full bg-orange-500 mt-2"></span>
                  <span>Refusal to investigate without explanation</span>
                </li>
              </ul>
            </div>

            {/* Romy can help */}
            <div className="mb-8 bg-pink-50 dark:bg-pink-950 rounded-xl p-6 border border-pink-200 dark:border-pink-800">
              <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-4">Romy can help you with:</h2>
              <ul className="space-y-2 text-gray-700 dark:text-gray-300 mb-4">
                <li className="flex items-start space-x-2">
                  <span className="flex-shrink-0 w-2 h-2 rounded-full bg-pink-500 mt-2"></span>
                  <span>Second opinions</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="flex-shrink-0 w-2 h-2 rounded-full bg-pink-500 mt-2"></span>
                  <span>Specialist switches</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="flex-shrink-0 w-2 h-2 rounded-full bg-pink-500 mt-2"></span>
                  <span>Preparing a written follow-up message or portal note</span>
                </li>
              </ul>
              <div>
                <Link
                  to="/chat"
                  className="inline-flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-orange-500 to-pink-500 text-white font-semibold rounded-lg hover:from-orange-600 hover:to-pink-600 transition-all shadow-lg hover:shadow-xl"
                >
                  <ChatCircle size={20} weight="fill" />
                  <span>Chat with Romy</span>
                </Link>
              </div>
            </div>

            {/* After-Visit Reflection */}
            <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
              <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-4">After-Visit Reflection</h2>
              <ul className="space-y-3 text-gray-700 dark:text-gray-300 mb-4">
                <li className="flex items-start space-x-2">
                  <span className="flex-shrink-0 w-2 h-2 rounded-full bg-gray-600 mt-2"></span>
                  <span>Did you feel heard?</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="flex-shrink-0 w-2 h-2 rounded-full bg-gray-600 mt-2"></span>
                  <span>Were next steps clear?</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="flex-shrink-0 w-2 h-2 rounded-full bg-gray-600 mt-2"></span>
                  <span>Do you feel more or less confident than before?</span>
                </li>
              </ul>
              <p className="text-gray-700 dark:text-gray-300 italic">
                If "less," use Romy to draft a follow-up or plan next actions.
              </p>
            </div>
              </Fragment>
            ) : activeTab === 1 ? (
              <Fragment>
                <div className="mb-6">
                  <h2 className="text-3xl font-bold text-gray-800 dark:text-gray-100 mb-2">
                    Communicating Symptoms Clearly & Credibly
                  </h2>
                  <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed">
                    Help your provider understand what's actually happening.
                  </p>
                </div>
            <div className="bg-pink-50 dark:bg-pink-950 rounded-xl p-6 mb-8 border border-pink-200 dark:border-pink-800">
              <p className="text-base font-semibold text-gray-800 dark:text-gray-100 mb-2">Goal:</p>
              <p className="text-gray-700 dark:text-gray-300">
                Describe symptoms in a way that is specific, clinical, and hard to dismiss, without minimizing lived experience.
              </p>
            </div>

            {/* Translate Your Experience Section */}
            <div className="mb-10">
              <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-4">
                Translate Your Experience → Clinical Signal
              </h2>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                Providers are trained to respond to:
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                  <p className="font-semibold text-gray-800 dark:text-gray-100">Patterns</p>
                </div>
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                  <p className="font-semibold text-gray-800 dark:text-gray-100">Duration</p>
                </div>
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                  <p className="font-semibold text-gray-800 dark:text-gray-100">Severity</p>
                </div>
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                  <p className="font-semibold text-gray-800 dark:text-gray-100">Functional impact</p>
                </div>
              </div>

              {/* CLEAR Framework */}
              <div className="mb-8">
                <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-4">Use the CLEAR Framework</h3>
                <div className="space-y-4">
                  <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 border-l-4 border-orange-500">
                    <p className="font-bold text-gray-800 dark:text-gray-100 mb-2">
                      <span className="text-orange-600">C</span> — Context
                    </p>
                    <p className="text-gray-700 dark:text-gray-300">When did this start? What was happening around that time?</p>
                  </div>
                  <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 border-l-4 border-orange-500">
                    <p className="font-bold text-gray-800 dark:text-gray-100 mb-2">
                      <span className="text-orange-600">L</span> — Location & Sensation
                    </p>
                    <p className="text-gray-700 dark:text-gray-300">Where is it? What does it feel like (sharp, dull, burning, pressure, pulling)?</p>
                  </div>
                  <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 border-l-4 border-orange-500">
                    <p className="font-bold text-gray-800 dark:text-gray-100 mb-2">
                      <span className="text-orange-600">E</span> — Episodes & Patterns
                    </p>
                    <p className="text-gray-700 dark:text-gray-300">How often does it happen? Is it cyclical, constant, or triggered?</p>
                  </div>
                  <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 border-l-4 border-orange-500">
                    <p className="font-bold text-gray-800 dark:text-gray-100 mb-2">
                      <span className="text-orange-600">A</span> — Aggravators & Relievers
                    </p>
                    <p className="text-gray-700 dark:text-gray-300">What makes it worse or better (movement, food, stress, cycle phase, meds)?</p>
                  </div>
                  <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 border-l-4 border-orange-500">
                    <p className="font-bold text-gray-800 dark:text-gray-100 mb-2">
                      <span className="text-orange-600">R</span> — Results on Daily Life
                    </p>
                    <p className="text-gray-700 dark:text-gray-300">What does this interfere with (sleep, work, parenting, exercise, mood)?</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Before-the-Visit Prep */}
            <div className="mb-8 bg-pink-50 dark:bg-pink-950 rounded-xl p-6 border border-pink-200 dark:border-pink-800">
              <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-4">Before-the-Visit Prep</h2>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                Romy can help you generate:
              </p>
              <ul className="space-y-2 text-gray-700 dark:text-gray-300 mb-4">
                <li className="flex items-start space-x-2">
                  <span className="flex-shrink-0 w-2 h-2 rounded-full bg-pink-500 mt-2"></span>
                  <span>A 1–2 sentence symptom summary</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="flex-shrink-0 w-2 h-2 rounded-full bg-pink-500 mt-2"></span>
                  <span>A pattern statement ("This worsens during my luteal phase")</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="flex-shrink-0 w-2 h-2 rounded-full bg-pink-500 mt-2"></span>
                  <span>A functional impact statement</span>
                </li>
              </ul>
              <div>
                <Link
                  to="/chat"
                  className="inline-flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-orange-500 to-pink-500 text-white font-semibold rounded-lg hover:from-orange-600 hover:to-pink-600 transition-all shadow-lg hover:shadow-xl"
                >
                  <ChatCircle size={20} weight="fill" />
                  <span>Chat with Romy</span>
                </Link>
              </div>
            </div>

            {/* Example Output */}
            <div className="mb-8 bg-orange-50 dark:bg-orange-950 rounded-xl p-6 border border-orange-200 dark:border-orange-800">
              <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-4">Example Output</h2>
              <div className="bg-white dark:bg-gray-900 rounded-lg p-5 border border-orange-300 dark:border-orange-800">
                <p className="text-gray-700 dark:text-gray-300 italic">
                  "For the past 9 months, I've had pelvic pain that becomes sharp and stabbing during the week before my period. It interferes with sleep and has caused me to miss work 1–2 days per cycle."
                </p>
              </div>
            </div>

            {/* In-Visit Language to Use */}
            <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
              <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-4">In-Visit Language to Use</h2>
              <div className="space-y-3 text-gray-700 dark:text-gray-300">
                <div className="flex items-start space-x-2">
                  <span className="flex-shrink-0 w-2 h-2 rounded-full bg-gray-600 mt-2"></span>
                  <span className="italic">"The most disruptive symptom for me is…"</span>
                </div>
                <div className="flex items-start space-x-2">
                  <span className="flex-shrink-0 w-2 h-2 rounded-full bg-gray-600 mt-2"></span>
                  <span className="italic">"What concerns me most is the pattern I'm noticing…"</span>
                </div>
                <div className="flex items-start space-x-2">
                  <span className="flex-shrink-0 w-2 h-2 rounded-full bg-gray-600 mt-2"></span>
                  <span className="italic">"This isn't occasional—it's affecting my ability to function."</span>
                </div>
              </div>
              </div>
              </Fragment>
            ) : activeTab === 2 ? (
              <Fragment>
                <div className="mb-6">
                  <h2 className="text-3xl font-bold text-gray-800 dark:text-gray-100 mb-2">
                    Requesting Tests, Referrals, or Further Evaluation
                  </h2>
                  <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed">
                    How to ask for more without sounding confrontational.
                  </p>
                </div>
            <div className="bg-pink-50 dark:bg-pink-950 rounded-xl p-6 mb-8 border border-pink-200 dark:border-pink-800">
              <p className="text-base font-semibold text-gray-800 dark:text-gray-100 mb-2">Goal:</p>
              <p className="text-gray-700 dark:text-gray-300">
                Request diagnostics or referrals collaboratively, while still being firm and informed.
              </p>
            </div>

            {/* Curiosity + Evidence + Clarity */}
            <div className="mb-10">
              <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-4">
                Curiosity + Evidence + Clarity
              </h2>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                Providers respond best when requests are framed as:
              </p>
              <div className="space-y-3 mb-8">
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 border-l-4 border-orange-500">
                  <p className="text-gray-700 dark:text-gray-300">A logical next step</p>
                </div>
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 border-l-4 border-orange-500">
                  <p className="text-gray-700 dark:text-gray-300">Tied to symptoms or guidelines</p>
                </div>
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 border-l-4 border-orange-500">
                  <p className="text-gray-700 dark:text-gray-300">Aligned with shared goals</p>
                </div>
              </div>

              {/* ASK Method */}
              <div className="mb-8">
                <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-4">Use the ASK Method</h3>
                <div className="space-y-4">
                  <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 border-l-4 border-orange-500">
                    <p className="font-bold text-gray-800 dark:text-gray-100 mb-1">
                      <span className="text-orange-600">A</span> — Anchor to Symptoms
                    </p>
                    <p className="text-gray-700 dark:text-gray-300 italic">"Given the frequency and severity of these symptoms…"</p>
                  </div>
                  <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 border-l-4 border-orange-500">
                    <p className="font-bold text-gray-800 dark:text-gray-100 mb-1">
                      <span className="text-orange-600">S</span> — Signal Awareness
                    </p>
                    <p className="text-gray-700 dark:text-gray-300 italic">"I've learned that these symptoms can sometimes be associated with…"</p>
                  </div>
                  <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 border-l-4 border-orange-500">
                    <p className="font-bold text-gray-800 dark:text-gray-100 mb-1">
                      <span className="text-orange-600">K</span> — Kind, Direct Request
                    </p>
                    <p className="text-gray-700 dark:text-gray-300 italic">"Would it make sense to evaluate that with ___?"</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Sample Request Scripts */}
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-4">Sample Request Scripts</h2>
              
              {/* Testing */}
              <div className="mb-6">
                <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-3">Testing</h3>
                <div className="space-y-3">
                  <div className="bg-white dark:bg-gray-900 rounded-lg p-5 border border-gray-200 dark:border-gray-700 shadow-sm">
                    <p className="text-gray-700 dark:text-gray-300 italic">"Would it be reasonable to run labs to rule out ___?"</p>
                  </div>
                  <div className="bg-white dark:bg-gray-900 rounded-lg p-5 border border-gray-200 dark:border-gray-700 shadow-sm">
                    <p className="text-gray-700 dark:text-gray-300 italic">"What tests would typically be used to evaluate this pattern?"</p>
                  </div>
                </div>
              </div>

              {/* Imaging */}
              <div className="mb-6">
                <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-3">Imaging</h3>
                <div className="bg-white dark:bg-gray-900 rounded-lg p-5 border border-gray-200 dark:border-gray-700 shadow-sm">
                  <p className="text-gray-700 dark:text-gray-300 italic">"At what point would imaging be appropriate for symptoms like this?"</p>
                </div>
              </div>

              {/* Referrals */}
              <div className="mb-6">
                <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-3">Referrals</h3>
                <div className="bg-white dark:bg-gray-900 rounded-lg p-5 border border-gray-200 dark:border-gray-700 shadow-sm">
                  <p className="text-gray-700 dark:text-gray-300 italic">"Is this something a specialist might be better equipped to evaluate?"</p>
                </div>
              </div>
            </div>

            {/* If the Provider Hesitates */}
            <div className="mb-8 bg-orange-50 dark:bg-orange-950 rounded-xl p-6 border border-orange-200 dark:border-orange-800">
              <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-4">If the Provider Hesitates: Respectful Persistence</h2>
              <p className="text-gray-700 dark:text-gray-300 mb-4">You have the right to ask:</p>
              <div className="space-y-3 text-gray-700 dark:text-gray-300">
                <div className="flex items-start space-x-2">
                  <span className="flex-shrink-0 w-2 h-2 rounded-full bg-orange-500 mt-2"></span>
                  <span className="italic">"Can you help me understand why this wouldn't be indicated right now?"</span>
                </div>
                <div className="flex items-start space-x-2">
                  <span className="flex-shrink-0 w-2 h-2 rounded-full bg-orange-500 mt-2"></span>
                  <span className="italic">"What would need to change for us to reconsider this?"</span>
                </div>
              </div>
            </div>

            {/* Documentation Strategy */}
            <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
              <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-4">Documentation Strategy (Advanced)</h2>
              <p className="text-gray-700 dark:text-gray-300 mb-3">If a request is declined:</p>
              <div className="bg-white dark:bg-gray-900 rounded-lg p-5 border border-gray-300 dark:border-gray-700">
                <p className="text-gray-700 dark:text-gray-300 italic">
                  "Could you please note in my chart that I asked about this and that it was deferred?"
                </p>
              </div>
            </div>
              </Fragment>
            ) : null}
          </div>
        </div>
      </main>

      <AppFooter />
    </div>
  );
}

